import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { useCreateShipment } from './useCreateShipment';
import { useGetShipmentElabel } from './useGetShipmentElabel';
import { useShipmentLabelUrls } from './useShipmentLabelUrls';
import { useUploadLabelImage } from './useUploadLabelImage';
import {
  ICreateShipmentRequest,
  IModifiedShipment,
  TShipmentTypeValue,
} from '../models/shipment';
import {
  ADDRESS_SUFFIXES,
  buildDuplicateFormSchema,
  IDuplicateFormValues,
} from '../schemas/duplicateShipmentFormSchema';
import { mapShipmentToCreateShipment } from '../utils/mapShipmentToCreateShipment';
import { useGetPartner } from '@/features/lp-village';
import { ICreateShipmentAddress } from '@/models/address';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

// ── Helpers ─────────────────────────────────────────────────────────────────

const CARRIER_KEY_TO_CODE: Record<string, string> = {
  CARRIER_CODE_UPS: 'UPS',
  CARRIER_CODE_FEDEX: 'FEDEX',
  CARRIER_CODE_TNTEXPRESS: 'TNTexpress',
  CARRIER_CODE_CHRONOPOST: 'CHRONOPOST',
  CARRIER_CODE_DHL: 'DHL',
  CARRIER_CODE_COLLISSIMO: 'COLISSIMO',
  CARRIER_CODE_MIIST: 'MIIST',
  CARRIER_CODE_CHUNGHWAPOST: 'ChunghwaPost',
  CARRIER_CODE_OTHER: 'Other',
};

const LP_CARRIER_OPTION = { carrierCode: 'LPCarrier', label: 'LPCarrier' };

/** Maps from ADDRESS_SUFFIXES keys to the corresponding ICreateShipmentAddress field */
const SUFFIX_TO_ADDRESS_KEY: Record<string, keyof ICreateShipmentAddress> = {
  Company: 'company',
  FirstName: 'firstName',
  LastName: 'lastName',
  Street: 'street',
  City: 'city',
  PostalCode: 'postalCode',
  CountryCode: 'countryCode',
  PhoneNumber: 'phoneNumber',
};

const flattenAddress = (
  prefix: string,
  address: ICreateShipmentAddress | undefined,
  email: string
): Record<string, string> => {
  const result: Record<string, string> = {
    [`${prefix}ContactType`]: address?.company ? 'professional' : 'personal',
    [`${prefix}Email`]: email || '',
  };

  for (const suffix of ADDRESS_SUFFIXES) {
    if (suffix === 'ContactType' || suffix === 'Email') continue;
    const addressKey = SUFFIX_TO_ADDRESS_KEY[suffix];
    if (addressKey) {
      result[`${prefix}${suffix}`] = (address?.[addressKey] as string) || '';
    }
  }

  return result;
};

const getField = (values: IDuplicateFormValues, key: string): string =>
  String((values as unknown as Record<string, unknown>)[key] ?? '');

const collectAddress = (
  values: IDuplicateFormValues,
  prefix: string,
  includePhone: boolean
): {
  address: ICreateShipmentAddress;
  notificationEmail: string;
  phoneNumber?: string;
} => {
  const address: ICreateShipmentAddress = {
    street2: '',
    houseNumber: '',
    state: '',
  } as ICreateShipmentAddress;

  for (const suffix of ADDRESS_SUFFIXES) {
    if (
      suffix === 'ContactType' ||
      suffix === 'Email' ||
      suffix === 'PhoneNumber'
    )
      continue;
    const addressKey = SUFFIX_TO_ADDRESS_KEY[suffix];
    if (addressKey) {
      address[addressKey] = getField(values, `${prefix}${suffix}`);
    }
  }

  const phoneNumber = includePhone
    ? getField(values, `${prefix}PhoneNumber`)
    : undefined;

  return {
    address,
    notificationEmail: getField(values, `${prefix}Email`),
    ...(phoneNumber && { phoneNumber }),
  };
};

const buildShipmentRequest = (
  values: IDuplicateFormValues,
  senderPrefix: string,
  recipientPrefix: string,
  options: {
    serviceLevel: string;
    carrierCode: string;
    reference: string;
    trackingCode: string;
    includePhone: boolean;
    serviceType?: string;
    type?: TShipmentTypeValue;
    boxId?: string;
    originalShipmentId?: string;
    providerStationId?: string;
  }
): ICreateShipmentRequest => ({
  sender: collectAddress(values, senderPrefix, options.includePhone),
  recipient: collectAddress(values, recipientPrefix, options.includePhone),
  serviceLevel: options.serviceLevel,
  weightInGrams: values.weightInGrams || '0',
  carrierCode: options.carrierCode,
  partnerId: values.partnerId,
  reference: options.reference,
  carrierTrackingCode: options.trackingCode,
  ...(options.originalShipmentId && {
    originalShipmentId: options.originalShipmentId,
  }),
  ...(options.serviceType && { serviceType: options.serviceType }),
  ...(options.type && { type: options.type }),
  ...(options.boxId && { boxId: options.boxId }),
  ...(options.providerStationId && {
    providerStationId: options.providerStationId,
  }),
});

const base64ToFile = (base64: string, filename: string): File => {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], filename, { type: 'image/png' });
};

// ── Hook ────────────────────────────────────────────────────────────────────

export const useDuplicateShipmentFormView = (
  sourceShipment: IModifiedShipment,
  originalServiceLevelId: string,
  onClose: () => void
) => {
  const addToast = useToasts((state) => state.addItem);
  const navigate = useNavigate();

  const [, createShipment] = useCreateShipment();
  const uploadLabelImage = useUploadLabelImage();
  const getELabel = useGetShipmentElabel();

  const initialData = useMemo(
    () => mapShipmentToCreateShipment(sourceShipment, originalServiceLevelId),
    // Reason: Intentionally capture only the first render values — the form should not reset when the source shipment reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [file, setFile] = useState<File | null>(null);
  const [returnFile, setReturnFile] = useState<File | null>(null);
  const [currentPartnerId, setCurrentPartnerId] = useState(
    initialData.partnerId
  );
  const [currentServiceLevel, setCurrentServiceLevel] = useState<string>(
    initialData.shipment.serviceLevel
  );

  const { data: partnerV3 } = useGetPartner(currentPartnerId);
  const { outboundLabelUrl, returnLabelUrl } = useShipmentLabelUrls(
    sourceShipment.id,
    sourceShipment.returnShipment?.id,
    file,
    returnFile
  );

  const smsEnabled = partnerV3?.sms_enabled ?? false;
  const serviceTypeEnabled = partnerV3?.service_type_enabled ?? false;
  const serviceTypeOptions = partnerV3?.service_types ?? [];
  const showReturnSection = !sourceShipment.originalShipmentId;
  const partnerName = partnerV3?.partner_id_card?.client_name ?? '';

  // Reason: Schema has no dynamic dependency — built once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validationSchema = useMemo(() => buildDuplicateFormSchema(), []);

  const initialValues = useMemo(
    () =>
      ({
        partnerId: initialData.partnerId,
        selectedPartner: '',
        serviceType: '',
        type: initialData.type ?? '',
        boxId: initialData.boxId,
        boxLabel: initialData.boxThingName || '',
        providerStationId: initialData.providerStationId ?? '',
        carrier: initialData.carrier,
        reference: initialData.reference,
        carrierTrackingCode: initialData.carrierTrackingCode,
        weightInGrams: initialData.shipment.weightInGrams,
        ...flattenAddress(
          'sender',
          initialData.shipment.sender.address,
          initialData.shipment.sender.notificationEmail
        ),
        ...flattenAddress(
          'recipient',
          initialData.shipment.recipient.address,
          initialData.shipment.recipient.notificationEmail
        ),
        addReturn: initialData.hasReturn,
        returnCarrier: initialData.returnCarrier,
        returnReference: initialData.returnReference,
        returnCarrierTrackingCode: initialData.returnCarrierTrackingCode,
        ...flattenAddress(
          'returnSender',
          initialData.returnShipment.sender.address,
          initialData.returnShipment.sender.notificationEmail
        ),
        ...flattenAddress(
          'returnRecipient',
          initialData.returnShipment.recipient.address,
          initialData.returnShipment.recipient.notificationEmail
        ),
      }) as IDuplicateFormValues,
    [initialData]
  );

  const handleFormSubmit = useCallback(
    async (values: IDuplicateFormValues) => {
      const sharedOptions = {
        serviceLevel: currentServiceLevel,
        includePhone: smsEnabled,
        serviceType: values.serviceType || undefined,
        type: (values.type as TShipmentTypeValue) || undefined,
        boxId: values.boxId || undefined,
        providerStationId: values.providerStationId || undefined,
      };

      const outboundRequest = buildShipmentRequest(
        values,
        'sender',
        'recipient',
        {
          ...sharedOptions,
          carrierCode: values.carrier,
          reference: values.reference || '',
          trackingCode: values.carrierTrackingCode || '',
        }
      );

      const { success, data } = await createShipment(outboundRequest);

      if (!success) return;

      const newShipmentId = data?.shipment?.id;
      const newShipmentVersionHash = data?.shipment?.versionHash;

      if (file && newShipmentId) {
        try {
          await uploadLabelImage(newShipmentId, newShipmentVersionHash, file);
        } catch {
          addToast(
            {
              msg: 'Shipment created but outbound label upload failed',
              type: MessageType.Warning,
            },
            'root',
            { persistBetweenViews: true }
          );
        }
      }

      let returnCreationFailed = false;

      if (values.addReturn) {
        const returnRequest = buildShipmentRequest(
          values,
          'returnSender',
          'returnRecipient',
          {
            ...sharedOptions,
            carrierCode: values.returnCarrier || values.carrier,
            reference: values.returnReference || '',
            trackingCode: values.returnCarrierTrackingCode || '',
            originalShipmentId: newShipmentId,
          }
        );

        const { success: retSuccess, data: retData } =
          await createShipment(returnRequest);

        if (retSuccess && returnFile && retData?.shipment?.id) {
          try {
            await uploadLabelImage(
              retData.shipment.id,
              retData.shipment.versionHash,
              returnFile
            );
          } catch {
            addToast(
              {
                msg: 'Return shipment created but label upload failed',
                type: MessageType.Warning,
              },
              'root',
              { persistBetweenViews: true }
            );
          }
        }

        if (!retSuccess) {
          addToast(
            {
              msg: 'Outbound shipment created but return shipment failed',
              type: MessageType.Warning,
            },
            'root',
            { persistBetweenViews: true }
          );
          returnCreationFailed = true;
        }
      }

      if (!returnCreationFailed) {
        addToast(
          {
            msg: 'Shipment successfully duplicated',
            type: MessageType.Success,
          },
          'root',
          { persistBetweenViews: true }
        );
      }

      onClose();
      if (newShipmentId) {
        navigate(`/shipments/${newShipmentId}`);
      }
    },
    [
      currentServiceLevel,
      smsEnabled,
      file,
      returnFile,
      createShipment,
      uploadLabelImage,
      addToast,
      onClose,
      navigate,
    ]
  );

  const formik = useFormik<IDuplicateFormValues>({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: false,
    onSubmit: handleFormSubmit,
  });

  const formValues = formik.values as unknown as Record<string, string>;
  const formErrors = formik.errors as unknown as Record<
    string,
    string | undefined
  >;
  const formTouched = formik.touched as unknown as Record<
    string,
    boolean | undefined
  >;

  const getAddressFormProps = useCallback(
    (prefix: string) => ({
      prefix,
      values: formValues,
      errors: formErrors,
      touched: formTouched,
      handleChange: formik.handleChange,
      handleBlur: formik.handleBlur,
      setFieldValue: formik.setFieldValue,
      showPhone: smsEnabled,
    }),
    [
      formValues,
      formErrors,
      formTouched,
      formik.handleChange,
      formik.handleBlur,
      formik.setFieldValue,
      smsEnabled,
    ]
  );

  const getAddressSummary = useCallback(
    (prefix: string) => ({
      company: formValues[`${prefix}Company`] ?? '',
      firstName: formValues[`${prefix}FirstName`] ?? '',
      lastName: formValues[`${prefix}LastName`] ?? '',
      street: formValues[`${prefix}Street`] ?? '',
      city: formValues[`${prefix}City`] ?? '',
      postalCode: formValues[`${prefix}PostalCode`] ?? '',
      countryCode: formValues[`${prefix}CountryCode`] ?? '',
      phoneNumber: formValues[`${prefix}PhoneNumber`] ?? '',
      street2: '',
      houseNumber: '',
      state: '',
    }),
    [formValues]
  );

  const hasAddressError = useCallback(
    (prefix: string) => {
      if (formik.submitCount === 0) return false;
      const prefixKeys = Object.keys(formik.errors).filter((k) =>
        k.startsWith(prefix)
      );

      return prefixKeys.some((k) => !!formErrors[k]);
    },
    [formik.submitCount, formik.errors, formErrors]
  );

  const handlePartnerChange = useCallback(
    (id: string) => {
      formik.setFieldValue('partnerId', id);
      setCurrentPartnerId(id);
    },
    // Reason: formik.setFieldValue is stable across renders (Formik guarantee) — adding formik would cause unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue]
  );

  const handleServiceLevelChange = useCallback((serviceLevel: string) => {
    setCurrentServiceLevel(serviceLevel);
  }, []);

  const carrierOptions = useMemo(() => {
    const partnerCarriers = (partnerV3?.carriers ?? [])
      .map((key) => CARRIER_KEY_TO_CODE[key])
      .filter(Boolean)
      .map((code) => ({ carrierCode: code, label: code }));

    return [LP_CARRIER_OPTION, ...partnerCarriers];
  }, [partnerV3?.carriers]);

  // Pre-fill label files from source shipment so the PATCH /label/overwrite
  // is automatically triggered after shipment creation (copy source label).
  useEffect(() => {
    const prefillLabels = async () => {
      try {
        const label = await getELabel(sourceShipment.id);
        if (label.img.image) {
          setFile(base64ToFile(label.img.image, 'source-label.png'));
        }
      } catch {
        // Source label not available — user can still manually upload
      }

      const returnId = sourceShipment.returnShipment?.id;
      if (!returnId) return;

      try {
        const returnLabel = await getELabel(returnId);
        if (returnLabel.img.image) {
          setReturnFile(
            base64ToFile(returnLabel.img.image, 'source-return-label.png')
          );
        }
      } catch {
        // Return label not available
      }
    };

    void prefillLabels();
    // Reason: Only run on mount — source shipment identity does not change while drawer is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset service type when partner changes (different partner may have different service types)
  useEffect(() => {
    formik.setFieldValue('serviceType', '');
    // Reason: Only react to partner identity change, not formik reference (which changes every render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerV3?.id]);

  return {
    formik,
    initialData,
    carrierOptions,
    smsEnabled,
    serviceTypeEnabled,
    serviceTypeOptions,
    showReturnSection,
    partnerName,
    outboundLabelUrl,
    returnLabelUrl,
    file,
    setFile,
    returnFile,
    setReturnFile,
    getAddressFormProps,
    getAddressSummary,
    hasAddressError,
    handlePartnerChange,
    handleServiceLevelChange,
  };
};
